import { useTranslation } from "react-i18next"
import { Plus } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  createProgressNoteHandler,
  validateProgressNoteCreatable,
} from "@/modules/patients/detail/episode-of-care/lib/create-progress-note"
import { EpisodeOfCareNode } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { getProgressNoteKind } from "@/modules/patients/detail/progress-note/lib/get-progress-note-kind"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useIsInContextOfAppointment } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

interface AllProgressNoteProps {
  dataPatientEpisodesOfCareData: EpisodeOfCareNode[] | undefined
  appointmentId: string | null
  patientId: string
  isEditMode: boolean
}

export const AllProgressNote = (props: AllProgressNoteProps) => {
  const { dataPatientEpisodesOfCareData, appointmentId, patientId, isEditMode } = props
  const { t } = useTranslation()
  const { userName } = useUser()
  const isInContextOfAppointment = useIsInContextOfAppointment()

  const setSelectedEpisodeOfCare = useEpisodeOfCareStoreContext((state) => state.setEpisodeOfCareFragmentReference)
  const selectedEpisodeOfCareFragmentReference = useEpisodeOfCareStoreContext(
    (state) => state.episodeOfCareFragmentReference,
  )

  const contextAppointment = useContextAppointment()

  const countryCode = useCountryParam()

  const isSelected = selectedEpisodeOfCareFragmentReference === null

  const progressNoteKind = contextAppointment ? getProgressNoteKind(contextAppointment.service) : null

  const isCreatable = validateProgressNoteCreatable({
    appointmentId,
    dataPatientEpisodesOfCareData,
    isInContextOfAppointment,
    progressNoteKind,
    userName,
  })

  const numberOfProgressNotes =
    dataPatientEpisodesOfCareData?.flatMap((e) => e.progressNotes?.edges?.map((e) => e?.node.id))?.length ?? 0

  const handleCreateProgressNote = () => {
    if (contextAppointment) {
      createProgressNoteHandler({
        appointmentId: contextAppointment.id,
        countryCode,
        dataPatientEpisodesOfCareData,
        patientId,
        progressNoteKind,
        language: contextAppointment.language,
      })
    }
  }

  return (
    <div className="grid pb-2">
      <div
        onClick={() => setSelectedEpisodeOfCare(null)}
        className={cn(
          "group/ep grid-cols-1fr-auto text-on-surface-subdued hover:bg-surface-hovered focus-visible:outline-primary-focused active:bg-primary-surface-focused grid h-12 cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 transition-all focus-visible:outline-offset-2",
          isSelected && "bg-primary-surface text-on-primary-surface! hover:bg-primary-surface-hovered",
        )}
        data-testid="all-progress-notes">
        <div className="grid">
          <div className={cn("text-on-surface truncate text-sm font-medium", isSelected && "text-on-primary-surface!")}>
            {t("episodeOfCare.allProgressNotes")}
          </div>
        </div>
        <Badge
          className={cn(
            "bg-surface-subdued text-on-surface-subdued h-5 min-w-5 justify-center border-none p-1 leading-4 font-normal",
            isEditMode &&
              isInContextOfAppointment &&
              contextAppointment?.isCompleted === false &&
              "group-hover/ep:hidden",
          )}
          data-testid="all-progress-notes-number">
          {numberOfProgressNotes}
        </Badge>
        {isEditMode && isInContextOfAppointment && contextAppointment?.isCompleted === false && (
          <div className="ml-auto hidden group-hover/ep:inline-flex">
            <Button
              variant="circle"
              className="h-10 w-10"
              data-testid="new-draft-progress-note-btn"
              disabled={!isCreatable}
              onClick={isCreatable ? handleCreateProgressNote : undefined}>
              <Plus className="text-on-surface-subdued" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
